import type { PublicUser, SessionInfo } from '@crowdset/contracts';
import { API } from '@crowdset/contracts/constants';
import { Avatar } from '@crowdset/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import { useEffect, useRef } from 'react';
import { api, errorMessage, setCsrfToken } from '../lib/api';
import { qk, useDevUsers, useSession } from '../lib/queries';
import { Badge, Button, EmptyState, LinkButton, Skeleton } from '../ui/kit';

/** Only same-origin, path-only return targets are honoured. */
export function safeReturnTo(v: string | null): string {
  if (!v || !v.startsWith('/') || v.startsWith('//') || v.includes('\\')) return '/';
  return v;
}

export function SignIn() {
  const session = useSession();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'));
  const s = session.data;
  const devUsers = useDevUsers(s?.authMode === 'local');
  const qc = useQueryClient();
  const nav = useNavigate();
  const login = useMutation({
    mutationFn: (handle: string) => api.post<SessionInfo>(API.devLogin, { handle }),
    onSuccess: (info) => {
      setCsrfToken(info.csrfToken);
      qc.setQueryData(qk.session, info);
      void qc.invalidateQueries();
      nav(returnTo, { replace: true });
    },
  });

  if (!s) return null;
  if (s.user) return <Navigate to={returnTo} replace />;

  return (
    <div className="mx-auto grid w-full max-w-[1100px] flex-1 grid-cols-1 items-center gap-10 px-4 py-14 md:grid-cols-[1fr_1.1fr]">
      <div>
        <h1 className="text-4xl font-semibold leading-none tracking-tighter md:text-5xl">
          Sign in to take the booth.
        </h1>
        <p className="mt-4 max-w-[46ch] text-base leading-relaxed text-fg-muted">
          Guests can listen. A verified account lets you chat, react, DJ, save playlists and create
          rooms.
        </p>
      </div>
      <div className="rounded-[1.75rem] border border-white/[0.07] bg-ink-900 p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
        {s.authMode === 'cognito' ? (
          <div className="flex flex-col gap-3">
            {params.get('error') && (
              <p
                role="alert"
                className="rounded-xl border border-danger/30 bg-danger/10 p-3 text-sm"
              >
                {params.get('error')}
              </p>
            )}
            {s.features.googleSignIn && (
              <>
                {/* Google's dark-theme sign-in button style (branding guidelines). */}
                <a
                  href={API.authGoogle(returnTo)}
                  className="inline-flex h-12 items-center justify-center gap-3 rounded-xl border border-[#8e918f] bg-[#131314] px-5 text-[15px] font-medium text-[#e3e3e3] hover:bg-[#1f1f21]"
                >
                  <GoogleMark />
                  Continue with Google
                </a>
                <div className="flex items-center gap-3 text-xs text-fg-subtle" aria-hidden>
                  <span className="h-px flex-1 bg-white/10" /> or{' '}
                  <span className="h-px flex-1 bg-white/10" />
                </div>
              </>
            )}
            <a
              href={API.authLogin(returnTo)}
              className="inline-flex h-12 items-center justify-center rounded-xl bg-accent px-5 text-[15px] font-medium text-accent-ink hover:bg-accent-strong"
            >
              Sign in with email
            </a>
            <a
              href={API.authSignup(returnTo)}
              className="inline-flex h-12 items-center justify-center rounded-xl border border-white/10 px-5 text-[15px] font-medium hover:bg-white/[0.05]"
            >
              Create an account
            </a>
            <p className="text-xs leading-relaxed text-fg-subtle">
              You will continue on the secure sign-in page, which also handles email verification
              and password resets. CrowdSet only receives a session cookie — never your password.
            </p>
          </div>
        ) : (
          <div>
            <div className="mb-4 flex items-center gap-2">
              <h2 className="text-lg font-semibold tracking-tight">Local development sign-in</h2>
              <Badge tone="warn">Seeded identities</Badge>
            </div>
            <p className="mb-4 text-sm text-fg-muted">
              This server runs with <code className="font-mono text-xs">AUTH_MODE=local</code>. Pick
              a seeded person. Production refuses this mode.
            </p>
            {devUsers.isPending ? (
              <div className="grid grid-cols-2 gap-2">
                {Array.from({ length: 4 }, (_, i) => (
                  <Skeleton key={i} className="h-16" />
                ))}
              </div>
            ) : devUsers.isError ? (
              <EmptyState title="Seeded users unavailable" body={errorMessage(devUsers.error)} />
            ) : (
              <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {(devUsers.data ?? []).map((u: PublicUser) => (
                  <li key={u.id}>
                    <button
                      className="flex w-full items-center gap-3 rounded-2xl border border-white/[0.07] px-3 py-2 text-left transition-colors hover:border-accent/50 hover:bg-accent/[0.04] disabled:opacity-50"
                      disabled={login.isPending}
                      onClick={() => login.mutate(u.handle)}
                      data-testid={`dev-login-${u.handle}`}
                    >
                      <span
                        className="flex h-11 w-10 items-end justify-center overflow-hidden rounded-xl bg-ink-800"
                        aria-hidden
                      >
                        <Avatar avatarId={u.avatarId} size={38} className="translate-y-2" />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium">{u.displayName}</span>
                        <span className="block truncate font-mono text-xs text-fg-subtle">
                          @{u.handle}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {login.error && (
              <p role="alert" className="mt-3 text-sm text-[#f3a3a4]">
                {errorMessage(login.error)}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Google "G" mark (standard four-colour logo). */
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

export function InviteAccept() {
  const { token = '' } = useParams();
  const session = useSession().data;
  const nav = useNavigate();
  const started = useRef(false);
  const accept = useMutation({
    mutationFn: () =>
      api.post<{ room: { slug: string; name: string } }>(API.acceptInvite, { token }),
    onSuccess: (r) => nav(`/r/${r.room.slug}`, { replace: true }),
  });
  useEffect(() => {
    if (session?.user && !started.current) {
      started.current = true;
      accept.mutate();
    }
  }, [session?.user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!session) return null;
  if (!session.user) {
    return (
      <div className="mx-auto w-full max-w-lg flex-1 px-4 py-20">
        <EmptyState
          title="You were invited to a private room"
          body="Sign in to accept the invite. The link works until it expires or is revoked."
          action={
            <LinkButton
              variant="primary"
              to={`/signin?returnTo=${encodeURIComponent(`/invite/${token}`)}`}
            >
              Sign in to accept
            </LinkButton>
          }
        />
      </div>
    );
  }
  return (
    <div className="mx-auto w-full max-w-lg flex-1 px-4 py-20">
      {accept.isError ? (
        <EmptyState
          title="This invite cannot be used"
          body={`${errorMessage(accept.error)} Ask the room owner for a new link.`}
          action={<LinkButton to="/">Browse rooms</LinkButton>}
        />
      ) : (
        <p className="text-sm text-fg-muted" role="status">
          Accepting invite…
        </p>
      )}
      {accept.isError && (
        <Button className="mt-3" variant="ghost" onClick={() => accept.mutate()}>
          Try again
        </Button>
      )}
    </div>
  );
}
